/**
 * A scenario block, read: its steps, and the comments beside them put where
 * they belong.
 *
 * The comments are the reason this exists. scenarios.yaml says WHY a step is
 * there - why 8 s, why this mask, what a threshold was measured from - in
 * comments, and a parser drops every one of them. So the text is read twice:
 * js-yaml gives the structure, and the YamlSource tokenizer (which already
 * knows a comment from a `#` inside a shell script) says on which line each
 * comment sits. A comment above a step becomes that step's WHY; one inside a
 * step, or after a value, becomes a note on the line it annotates.
 *
 * It must not INVENT. When the lines and the structure disagree - a step count
 * that does not match, a shape it does not know - the comments are left off
 * rather than attached to the wrong step, and a block that does not parse is
 * no view at all: the caller shows the text as written instead.
 */
import { load } from "js-yaml";
import { yamlPieces, type Piece } from "./YamlSource";

/** The keys that sit BESIDE a step's action. The harness's own list is
    actions.NON_VERB_KEYS; a test holds this copy to it. */
export const MODIFIERS = ["assert", "read_events", "reason", "save", "sudo"];

/** A comment, as prose: lines of one paragraph joined, a list kept a list. */
export type Prose = string[];
/** A comment inside a step, and the line it sits on or above: that line's
    key when it has one (`grab`), else the line itself (`[48, 376, 384, 508]`,
    `{json: pass, truthy: true}`), for the view to show as it sees fit. */
export type Note = { key: string; at: string; text: Prose };

export type StepView = {
  /** 1-based, the way the engine numbers the steps it records. */
  n: number;
  action: string;
  input: unknown;
  asserts: unknown[];
  save?: unknown;
  sudo?: boolean;
  /** Any other key beside the action (read_events, reason). */
  extra: [string, unknown][];
  why: Prose[];
  notes: Note[];
};

export type ScenarioView = {
  id: string;
  about?: string;
  precondition: StepView[];
  steps: StepView[];
  restore: unknown[];
  draft: boolean;
  runsLast: boolean;
  /** Comments above the steps: what the case as a whole is about. */
  header: Prose[];
  headerNotes: Note[];
  /** Comments after the last step - in practice beside `restore`. */
  after: Prose[];
  afterNotes: Note[];
  /** Whether the comments could be placed. False means they are left out -
      the source tab still has every one. */
  placed: boolean;
};

// Enough for any real scenario (the longest SNCF case is a few thousand
// nodes); what it stops is a block of nested aliases that expands to billions
// of nodes when drawn. The source tab is always there instead.
const NODE_BUDGET = 50000;

function tooBig(v: unknown): boolean {
  let left = NODE_BUDGET;
  const stack: unknown[] = [v];
  while (stack.length) {
    const x = stack.pop();
    if (--left < 0) return true;
    if (Array.isArray(x)) for (const y of x) stack.push(y);
    else if (x && typeof x === "object") for (const y of Object.values(x)) stack.push(y);
  }
  return false;
}

function asStep(raw: unknown, n: number, verbs?: string[]): StepView | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const o = raw as Record<string, unknown>;
  const keys = Object.keys(o);
  const action =
    keys.find((k) => verbs?.includes(k)) ?? keys.find((k) => !MODIFIERS.includes(k));
  if (!action) return null;
  const asserts = Array.isArray(o.assert) ? o.assert : o.assert != null ? [o.assert] : [];
  return {
    n,
    action,
    input: o[action],
    asserts,
    save: o.save,
    sudo: o.sudo === true,
    extra: keys
      .filter((k) => k !== action && !["assert", "save", "sudo"].includes(k))
      .map((k) => [k, o[k]]),
    why: [],
    notes: [],
  };
}

function steps(list: unknown, verbs?: string[]): StepView[] | null {
  if (list == null) return [];
  if (!Array.isArray(list)) return null;
  const out: StepView[] = [];
  for (const [i, raw] of list.entries()) {
    const s = asStep(raw, i + 1, verbs);
    if (!s) return null;
    out.push(s);
  }
  return out;
}

/** One line of the block, as the tokenizer cut it. */
type Line = {
  indent: number;
  blank: boolean;
  /** The whole line is a comment. */
  comment: boolean;
  /** The comment's text, without the `#`, whether whole-line or trailing. */
  said: string;
  /** What the line says before any comment, trimmed. */
  code: string;
  dash: boolean;
  key: string;
};

function line(pieces: Piece[]): Line {
  const text = pieces.map((p) => p.text).join("");
  const indent = text.length - text.trimStart().length;
  const solid = pieces.filter((p) => p.kind !== "plain");
  const c = solid.findIndex((p) => p.kind === "comment");
  const said = c >= 0 ? solid[c].text.replace(/^#+ ?/, "") : "";
  let code = "";
  for (const p of pieces) {
    if (p.kind === "comment") break;
    code += p.text;
  }
  const first = solid[0];
  const key = solid.find((p) => p.kind === "id" || p.kind === "action" || p.kind === "key");
  return {
    indent,
    blank: solid.length === 0,
    comment: c === 0,
    said,
    code: code.trim(),
    dash: first?.kind === "punct" && first.text === "-",
    key: key && (solid[0] === key || (first?.kind === "punct" && solid[1] === key)) ? key.text : "",
  };
}

/** Comment lines to paragraphs: an empty `#` breaks one; a line indented
    further keeps its own line, and so does a list item - but only in a list,
    i.e. after a line ending in `:` or after another item. Wrapped prose puts a
    number or a dash at the start of a line by accident ("...and EXITED" /
    "0. Measured on the bench") and must still read as one sentence. */
function prose(said: string[]): Prose[] {
  const paras: Prose[] = [];
  let cur: string[] = [];
  let inList = false;
  const close = () => {
    if (cur.length) paras.push(cur);
    cur = [];
    inList = false;
  };
  for (const s of said) {
    if (!s.trim()) {
      close();
      continue;
    }
    const item = /^\s*(?:[-*•]|\d+[.)])\s/.test(s);
    const listStarts: boolean = item && (!cur.length || inList || /:\s*$/.test(cur[cur.length - 1]));
    const own = /^\s/.test(s) || listStarts;
    if (cur.length && !own) cur[cur.length - 1] += " " + s.trim();
    else cur.push(own ? s.replace(/^\s+/, (w) => w.slice(0, 2)) : s.trim());
    if (item) inList = listStarts;
  }
  close();
  return paras;
}

function noteAt(code: string, text: Prose): Note {
  const at = code.replace(/^-\s+/, "");
  const m = at.match(/^("[^"]*"|'[^']*'|[A-Za-z0-9_$.-]+)\s*:(\s|$)/);
  return { key: m ? m[1] : "", at, text };
}

/** Put the comments of `text` on the view's steps. False when unsure. */
function place(view: ScenarioView, text: string): boolean {
  const lines = yamlPieces(text).map(line);
  const body = lines.findIndex((l, i) => i > 0 && !l.blank && !l.comment);
  const bodyIndent = body >= 0 ? lines[body].indent : 0;
  const at = lines.findIndex(
    (l, i) => i > 0 && l.indent === bodyIndent && l.key === "steps" && l.code === "steps:",
  );

  // Where each step starts and where the list ends, by indentation alone:
  // the first line under `steps:` fixes the column of the dashes.
  const starts: number[] = [];
  let end = at >= 0 ? at + 1 : lines.length;
  if (at >= 0) {
    let dashCol = -1;
    end = lines.length;
    for (let i = at + 1; i < lines.length; i++) {
      const l = lines[i];
      if (l.blank || l.comment) continue;
      if (dashCol < 0) {
        if (!l.dash || l.indent < bodyIndent) {
          end = i;
          break;
        }
        dashCol = l.indent;
      }
      if (l.dash && l.indent === dashCol) starts.push(i);
      else if (l.indent <= bodyIndent) {
        end = i;
        break;
      }
    }
  }
  if (starts.length !== view.steps.length) return false;

  // A run of comments belongs to the first line of code BELOW it: to a step
  // when that line starts one, to the line it annotates inside a step, and to
  // the case (above the steps) or to what follows them (`restore`) otherwise.
  const pending: string[] = [];
  const flush = (): Prose[] => prose(pending.splice(0));
  let step = -1;
  for (let i = 1; i < lines.length; i++) {
    const l = lines[i];
    if (l.comment) {
      pending.push(l.said);
      continue;
    }
    if (l.blank) {
      if (pending.length) pending.push("");
      continue;
    }
    const k = starts.indexOf(i);
    if (k >= 0) {
      step = k;
      view.steps[k].why.push(...flush(), ...(l.said ? prose([l.said]) : []));
      continue;
    }
    const where = at >= 0 && i > at && i < end ? "step" : i < (at >= 0 ? at + 1 : end) ? "header" : "after";
    const above = flush();
    const said = l.said ? prose([l.said]).flat() : [];
    if (where === "step" && step >= 0) {
      const notes = view.steps[step].notes;
      if (above.length) notes.push(noteAt(l.code, above.flat()));
      if (said.length) notes.push(noteAt(l.code, said));
    } else if (where === "header") {
      view.header.push(...above);
      if (said.length) view.headerNotes.push(noteAt(l.code, said));
    } else {
      view.after.push(...above);
      if (said.length) view.afterNotes.push(noteAt(l.code, said));
    }
  }
  view.after.push(...flush());
  return true;
}

/**
 * The block with every alias to an anchor it does not define turned into the
 * alias's name, as a string. A block is cut from a file, and an anchor can
 * live in another case of it: DOS-BAWU-DEPARTING-001 masks with
 * `*bawu_moving`, which DOS-BAWU-ATSTOP1-001 defines. Alone, that block does
 * not parse; with the name in place of the value it does, and the view says
 * "*bawu_moving" - which is what the text says too.
 */
function ownAliases(text: string): string {
  const lines = yamlPieces(text);
  const defined = new Set(
    lines.flat().filter((p) => p.kind === "anchor" && p.text.startsWith("&")).map((p) => p.text.slice(1)),
  );
  let changed = false;
  const out = lines.map((l) =>
    l
      .map((p) => {
        if (p.kind !== "anchor" || !p.text.startsWith("*") || defined.has(p.text.slice(1))) return p.text;
        changed = true;
        return JSON.stringify(p.text);
      })
      .join(""),
  );
  return changed ? out.join("\n") : text;
}

/**
 * The view of one block, or null when it is not a scenario this can show -
 * the caller then shows the text as written, which is never wrong.
 */
export function readScenario(text: string, verbs?: string[]): ScenarioView | null {
  let doc: unknown;
  try {
    doc = load(ownAliases(text), { json: true });
  } catch {
    return null;
  }
  if (!doc || typeof doc !== "object" || Array.isArray(doc)) return null;
  const ids = Object.keys(doc);
  if (ids.length !== 1) return null;
  const body = (doc as Record<string, unknown>)[ids[0]];
  if (!body || typeof body !== "object" || Array.isArray(body) || tooBig(body)) return null;
  const b = body as Record<string, unknown>;
  const main = steps(b.steps, verbs);
  const pre = steps(b.precondition, verbs);
  if (!main || !pre) return null;
  const view: ScenarioView = {
    id: ids[0],
    about: typeof b.about === "string" ? b.about : undefined,
    precondition: pre,
    steps: main,
    restore: Array.isArray(b.restore) ? b.restore : [],
    draft: b.draft === true,
    runsLast: b.runs_last === true,
    header: [],
    headerNotes: [],
    after: [],
    afterNotes: [],
    placed: false,
  };
  view.placed = place(view, text);
  if (!view.placed) {
    for (const s of view.steps) {
      s.why = [];
      s.notes = [];
    }
    view.header = [];
    view.headerNotes = [];
    view.after = [];
    view.afterNotes = [];
  }
  return view;
}
